import React, { useEffect, useState } from 'react';
import { resolveStorageImageUrl } from '../utils/storageImageUrl';

/**
 * Renders a "open the attached PDF" button for an announcement.
 *
 * WHY THE KEY LIVES IN `tags`
 * ---------------------------
 * The Announcement model has no attachment field. Rather than change the GraphQL
 * schema (which needs a backend deploy) for a presentation-only feature, the S3
 * object key is carried in the existing `tags` string array using an `pdf:` prefix,
 * e.g. `pdf:announcements/wrap-ups/july-2026.pdf`. This is deliberate, documented
 * debt — see BACKLOG. Promoting it to a real `attachmentKey` field later is a
 * drop-in change: only `extractAttachmentKey` below and the admin write path
 * would need to move.
 *
 * WHY IT IS NOT JUST A LINK IN THE CONTENT
 * ----------------------------------------
 * `announcements/*` grants `allow.guest.to(['read'])`, which is Cognito Identity
 * Pool (guest IAM role) access — NOT a public S3 bucket policy. A plain
 * `https://bucket.s3.../key` link carries no credentials and always 403s (see the
 * long comment in utils/storageImageUrl.ts, which documents this exact trap
 * breaking event images). The key must therefore be resolved to a signed URL at
 * render time via getUrl(), which is what resolveStorageImageUrl does.
 */

const TAG_PREFIX = 'pdf:';

/** Pulls the S3 key out of an announcement's tags, or null if there is no attachment. */
export function extractAttachmentKey(
  tags: (string | null)[] | null | undefined
): string | null {
  if (!tags) return null;
  for (const tag of tags) {
    if (typeof tag === 'string' && tag.startsWith(TAG_PREFIX)) {
      const key = tag.slice(TAG_PREFIX.length).trim();
      if (key) return key;
    }
  }
  return null;
}

interface AnnouncementAttachmentProps {
  tags: (string | null)[] | null | undefined;
  /** Button text, e.g. "Open the July wrap-up (PDF)". */
  label?: string;
}

const AnnouncementAttachment: React.FC<AnnouncementAttachmentProps> = ({ tags, label }) => {
  const storageKey = extractAttachmentKey(tags);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!storageKey) return;
    setFailed(false);
    resolveStorageImageUrl(storageKey)
      .then((resolved) => {
        if (cancelled) return;
        if (resolved) setUrl(resolved);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storageKey]);

  if (!storageKey) return null;

  // Presigned URLs expire (15 min). Resolve on click as well as on mount so a
  // page left open for a long time still opens a valid link.
  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const fresh = (await resolveStorageImageUrl(storageKey)) || url;
    if (fresh) window.open(fresh, '_blank', 'noopener,noreferrer');
    else setFailed(true);
  };

  if (failed) {
    return (
      <p className="mt-8 text-sm text-gray-500">
        The attached PDF could not be loaded. Please try again later or contact the club.
      </p>
    );
  }

  return (
    <div className="mt-8">
      <a
        href={url || '#'}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 bg-fishing-blue hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
      >
        <span aria-hidden="true" className="text-xl">
          📄
        </span>
        {label || 'Open the PDF'}
        <span aria-hidden="true">↗</span>
      </a>
      <p className="mt-2 text-xs text-gray-500">Opens in a new tab.</p>
    </div>
  );
};

export default AnnouncementAttachment;
